/**
 * Types pour les rapports de campagne
 */

import type { Json } from '@/integrations/supabase/types';

/**
 * Distribution des sentiments dans les transcriptions audio
 */
export interface SentimentDistribution {
  positive: number;
  neutral: number;
  negative: number;
}

/**
 * Synthèse IA générée pour un rapport
 */
export interface AISynthesis {
  resume: string;
  points_forts: Array<{
    titre: string;
    description: string;
  }>;
  axes_amelioration: Array<{
    titre: string;
    description: string;
  }>;
  conseils_manager: Array<{
    titre: string;
    description: string;
  }>;
  citation_illustrative: string | null;
  score_confiance: number;
  sentimentDistribution?: SentimentDistribution;
  relationConformity?: {
    conformCount: number;
    totalCount: number;
    conformityRate: number;
  };
  email_company_matching?: {
    summary: {
      totalCount: number;
      okCount: number;
      warningCount: number;
      criticalCount: number;
      averageScore: number;
    };
    results: Array<{
      referentName: string;
      referentEmail: string;
      score: number;
      status: 'OK' | 'WARNING' | 'CRITICAL';
      reason: string;
      isCoherent: boolean;
    }>;
  };
}

/**
 * Rapport de campagne complet
 */
export interface CampaignReport {
  id: string;
  campaign_id: string;
  pdf_url: string;
  candidate_view_url: string;
  ai_synthesis: string | AISynthesis; // Peut être string (JSON) ou objet parsé
  performance_avg: number;
  adaptability_avg: number;
  learning_avg: number;
  reliability_avg: number;
  communication_avg: number;
  overall_avg: number;
  sentiment_distribution?: string | Json; // Distribution du sentiment depuis la base
  generated_at: string;
  campaigns?: {
    candidate_name: string;
    candidate_email: string;
    target_position: string;
    status: string;
    selected_evaluation_criteria?: Json;
  };
}

/**
 * Helper pour parser ai_synthesis en toute sécurité
 * Gère les deux formats de nommage : score_confiance (frontend) et confiance_score (backend)
 * Convertit sentiment_analysis (backend) en sentimentDistribution (frontend)
 */
export function parseAISynthesis(ai_synthesis: string | AISynthesis): AISynthesis {
  if (typeof ai_synthesis === 'string') {
    try {
      const parsed = JSON.parse(ai_synthesis) as any;
      
      // Normaliser le nom du champ de confiance (backend utilise confiance_score, frontend score_confiance)
      if (parsed.confiance_score !== undefined && parsed.score_confiance === undefined) {
        parsed.score_confiance = parsed.confiance_score;
      }
      
      // Convertir sentiment_analysis (backend) en sentimentDistribution (frontend)
      if (parsed.sentiment_analysis && !parsed.sentimentDistribution) {
        parsed.sentimentDistribution = {
          positive: parsed.sentiment_analysis.positif || 0,
          neutral: parsed.sentiment_analysis.neutre || 0,
          negative: parsed.sentiment_analysis.negatif || 0,
        };
      }
      
      return parsed as AISynthesis;
    } catch (error) {
      console.error('Erreur parsing ai_synthesis:', error);
      // Retourner une structure par défaut en cas d'erreur
      return {
        resume: '',
        points_forts: [],
        axes_amelioration: [],
        conseils_manager: [],
        citation_illustrative: null,
        score_confiance: 0,
        sentimentDistribution: { positive: 0, neutral: 0, negative: 0 },
      };
    }
  }
  
  // Si c'est déjà un objet, normaliser aussi
  const synthesis = ai_synthesis as any;
  if (synthesis.confiance_score !== undefined && synthesis.score_confiance === undefined) {
    synthesis.score_confiance = synthesis.confiance_score;
  }
  
  // Convertir sentiment_analysis en sentimentDistribution
  if (synthesis.sentiment_analysis && !synthesis.sentimentDistribution) {
    synthesis.sentimentDistribution = {
      positive: synthesis.sentiment_analysis.positif || 0,
      neutral: synthesis.sentiment_analysis.neutre || 0,
      negative: synthesis.sentiment_analysis.negatif || 0,
    };
  }
  
  return synthesis as AISynthesis;
}
